import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Process {
  id: string
  name: string
  category: string
  efficiency_score: number
  throughput: number
  cycle_time_hours: number
  status: string
}

interface Metric {
  id: string
  name: string
  category: string
  value: number
  unit: string
  benchmark: number
  variance_percent: number
}

interface Statistics {
  total_processes: number
  active_processes: number
  avg_efficiency: number
  total_throughput: number
  bottlenecks_identified: number
  automation_rate: number
  cost_savings: number
  incidents_resolved: number
}

export default function OperationsIntelligencePage() {
  const [processes, setProcesses] = useState<Process[]>([])
  const [metrics, setMetrics] = useState<Metric[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [activeTab, setActiveTab] = useState<'processes' | 'metrics'>('processes')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [processRes, efficiencyRes, statsRes] = await Promise.all([
        fetch('/api/operationsintelligence/processes'),
        fetch('/api/operationsintelligence/efficiency'),
        fetch('/api/operationsintelligence/statistics')
      ])

      if (processRes.ok) setProcesses(await processRes.json())
      if (efficiencyRes.ok) setMetrics(await efficiencyRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching operations intelligence data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getStatusBadgeClass = (status: string) => {
    const classes: Record<string, string> = {
      optimal: 'bg-green-100 text-green-800',
      running: 'bg-blue-100 text-blue-800',
      degraded: 'bg-yellow-100 text-yellow-800',
      critical: 'bg-red-100 text-red-800',
      stopped: 'bg-gray-100 text-gray-800'
    }
    return classes[status.toLowerCase()] || 'bg-gray-100 text-gray-800'
  }

  const getEfficiencyColor = (score: number) => {
    if (score >= 90) return 'text-green-600'
    if (score >= 70) return 'text-yellow-600'
    return 'text-red-600'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Operations Intelligence</h1>
          <p className="mt-2 text-gray-600">AI-driven operational efficiency and process optimization</p>
        </div>

        {/* Statistics Cards */}
        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Processes</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.total_processes}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg Efficiency</p>
              <p className={`text-2xl font-bold ${getEfficiencyColor(statistics.avg_efficiency)}`}>
                {statistics.avg_efficiency.toFixed(1)}%
              </p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Bottlenecks</p>
              <p className="text-2xl font-bold text-yellow-600">{statistics.bottlenecks_identified}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Cost Savings</p>
              <p className="text-2xl font-bold text-green-600">${(statistics.cost_savings / 1000).toFixed(0)}K</p>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('processes')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'processes'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Processes ({processes.length})
            </button>
            <button
              onClick={() => setActiveTab('metrics')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'metrics'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Efficiency Metrics
            </button>
          </nav>
        </div>

        {/* Processes Table */}
        {activeTab === 'processes' && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Process</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Efficiency</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Throughput</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cycle Time</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {processes.map((process) => (
                  <tr key={process.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <p className="text-sm font-medium text-gray-900">{process.name}</p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {process.category}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                          <div
                            className={`h-2 rounded-full ${process.efficiency_score >= 90 ? 'bg-green-500' : process.efficiency_score >= 70 ? 'bg-yellow-500' : 'bg-red-500'}`}
                            style={{ width: `${process.efficiency_score}%` }}
                          ></div>
                        </div>
                        <span className="text-sm text-gray-600">{process.efficiency_score}%</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {process.throughput.toLocaleString()}/hr
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {process.cycle_time_hours.toFixed(1)}h
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadgeClass(process.status)}`}>
                        {process.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Metrics Cards */}
        {activeTab === 'metrics' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {metrics.map((metric) => (
              <div key={metric.id} className="bg-white rounded-lg shadow p-6">
                <div className="flex justify-between items-start">
                  <p className="text-sm text-gray-500">{metric.category}</p>
                  <span className={`text-sm font-medium ${metric.variance_percent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {metric.variance_percent >= 0 ? '+' : ''}{metric.variance_percent.toFixed(1)}%
                  </span>
                </div>
                <p className="text-lg font-semibold text-gray-900 mt-2">{metric.name}</p>
                <div className="mt-4">
                  <p className="text-3xl font-bold text-gray-900">
                    {metric.value.toLocaleString()} {metric.unit}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    Benchmark: {metric.benchmark.toLocaleString()} {metric.unit}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  )
}
